// HTML/CSS UI overlay. Builds the HUD, the card picker, the death/skill-tree
// screen, the offline report, and transient banners/toasts. Per-frame it only
// writes text into existing elements (never rebuilds the DOM) so it stays cheap
// and hover states survive. Gameplay actions are delegated to a controller so
// the UI stays decoupled from the systems.

import './ui.css';
import type { GameState } from '@core/state';
import { fmt, fmtInt, fmtRate, fmtDuration } from '@core/numbers';
import { GAME_NAME, VERSION } from '@data/version';
import { ageName, tierName } from '@data/lore';
import { RESOURCE_ORDER, RESOURCES } from '@data/resources';
import type { ResourceId } from '@data/resources';
import { BUILDINGS } from '@data/buildings';
import { nextPlannedBuilding } from '@systems/buildings';
import { BALANCE } from '@data/balance';
import { CARD_BY_ID, activeSynergies, type CardCategory } from '@data/cards';
import { SKILLS, SKILL_BY_ID, skillCost, skillUnlocked } from '@data/skills';
import { TECHS, techCost } from '@data/technologies';
import { ARTIFACT_BY_ID, activationCostAt } from '@data/artifacts';
import { computeIncome, settlementDefense, effectiveThreat } from '@systems/stats';
import { ageThreshold } from '@systems/ages';
import { waveName, gateBossName } from '@data/enemies';
import { planetName, conquestAge, cosmicGain } from '@data/planets';
import { canRetire } from '@systems/prestige';
import { NEWS, type NewsEntry } from '@data/news';
import { bus } from '@core/events';
import { t, tc, getLang, setLang, LANGS, type Lang } from '@i18n/i18n';
import { api, setToken, clearToken, type VerifyResult } from '@online/api';
import { track } from '@online/analytics';

export interface UIController {
  onPickCard(id: string): void;
  onBuySkill(id: string): void;
  onBuyTech(id: string): void;
  onActivateArtifact(id: string): void;
  onRestart(): void;
  onPrestige(): void;
  onCallWave(): void; // skip the wave timer: pull the next wave in now
  onRetire(): void; // end the civilization by choice (skill tree, then restart)
  onHardReset(): void; // wipe all progress (local + cloud) and start fresh
  onSignIn(): void; // a guest chose to link their local run to an account
  onToggleMute(): boolean; // returns the new muted state
}

const CAT_CLASS: Record<CardCategory, string> = {
  economy: 'eco',
  military: 'mil',
  knowledge: 'kno',
};

// ---- Google Identity Services (loaded on demand on the login screen) ----

interface GsiGlobal {
  accounts: {
    id: {
      initialize(cfg: { client_id: string; callback: (r: { credential: string }) => void }): void;
      renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
    };
  };
}

let gsiLoading: Promise<void> | null = null;
/** Loads the GIS script once; resolves even on failure (email flow still works). */
function loadGsi(): Promise<void> {
  if (gsiLoading) return gsiLoading;
  gsiLoading = new Promise((res) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => res();
    s.onerror = () => res();
    document.head.appendChild(s);
  });
  return gsiLoading;
}

export class UI {
  private root: HTMLElement;
  private ctrl: UIController;

  // cached element refs (set in build)
  private ageEl!: HTMLElement;
  private tierNameEl!: HTMLElement;
  private progressFill!: HTMLElement;
  private spoilsEl!: HTMLElement;
  private spEl!: HTMLElement;
  private cosmicEl!: HTMLElement;
  private ascendEl!: HTMLElement;
  private ascendBtn!: HTMLButtonElement;
  private muteBtn!: HTMLButtonElement;
  private resAmt: Record<string, HTMLElement> = {};
  private resRate: Record<string, HTMLElement> = {};
  private popEl!: HTMLElement;
  private autoEl!: HTMLElement;
  private bldEl!: HTMLElement;
  private nbName!: HTMLElement;
  private nbBars!: HTMLElement;
  private nbSig = '';
  private nbFills: Record<string, HTMLElement> = {};
  private nbNums: Record<string, HTMLElement> = {};
  private waveLabel!: HTMLElement;
  private waveCount!: HTMLElement;
  private waveDef!: HTMLElement;
  private waveThreat!: HTMLElement;
  private waveVerdict!: HTMLElement;
  private waveConquest!: HTMLElement;
  private waveBoss!: HTMLElement;

  private cardsOverlay!: HTMLElement;
  private cardsRow!: HTMLElement;
  private deathOverlay!: HTMLElement;
  private selectedSkill: string | null = null; // node tapped in the skill tree
  private waveNowBtn!: HTMLButtonElement;
  private retireBtn!: HTMLButtonElement;
  private retireArmedAt = 0; // two-tap confirm window for retiring
  private offlineOverlay!: HTMLElement;
  private banner!: HTMLElement;
  private toasts!: HTMLElement;
  private bannerTimer = 0;

  // periodic feedback nudge
  private nudgesShown = new Set<number>();
  private nudgeTimer = 0;

  // first-run tutorial (each step's text comes from t(key + '.title'/'.text'))
  private tutStep = 0;
  private tutorialOn = false;
  private readonly tutSteps: { sel: string | null; key: string }[] = [
    { sel: null, key: 'tut.s1' },
    { sel: '#resources', key: 'tut.s2' },
    { sel: '#identity', key: 'tut.s3' },
    { sel: '#wave', key: 'tut.s4' },
    { sel: '#meta', key: 'tut.s5' },
  ];

  // Phase 2: lab (techs + artifacts), synergies, buffs
  private labPanel!: HTMLElement;
  private labSpoils!: HTMLElement;
  private techList!: HTMLElement;
  private artifactList!: HTMLElement;
  private synergyStrip!: HTMLElement;
  private buffStrip!: HTMLElement;
  private techBuyBtns: Record<string, HTMLButtonElement> = {};
  private artifactBtns: Record<string, HTMLButtonElement> = {};
  private buffChips: Record<string, HTMLElement> = {};
  private lastSynergySig = '';
  private buffSig = '';
  private lastState: GameState | null = null; // for feedback metadata
  // Sentinel: a real artifact signature is '' or "id:0"-style, never "?", so
  // the first updateLab frame always paints the empty-state hint.
  private artifactSig = '?';

  constructor(root: HTMLElement, ctrl: UIController) {
    this.root = root;
    this.ctrl = ctrl;
  }

  build(initialMuted: boolean): void {
    this.root.innerHTML = this.skeleton();

    this.ageEl = this.q('#age-label');
    this.tierNameEl = this.q('#tier-name');
    this.progressFill = this.q('#progress-fill');
    this.spoilsEl = this.q('#chip-spoils');
    this.spEl = this.q('#chip-sp');
    this.cosmicEl = this.q('#chip-cosmic');
    this.ascendEl = this.q('#ascend');
    this.ascendBtn = this.q('#ascend-btn');
    this.muteBtn = this.q('#mute-btn');
    this.popEl = this.q('#stat-pop');
    this.autoEl = this.q('#stat-auto');
    this.bldEl = this.q('#stat-bld');
    this.nbName = this.q('#nb-name');
    this.nbBars = this.q('#nb-bars');
    this.waveLabel = this.q('#wave-label');
    this.waveCount = this.q('#wave-count');
    this.waveDef = this.q('#wave-def');
    this.waveThreat = this.q('#wave-threat');
    this.waveVerdict = this.q('#wave-verdict');
    this.waveConquest = this.q('#wave-conquest');
    this.waveBoss = this.q('#wave-boss');
    this.waveNowBtn = this.q('#wave-now-btn') as HTMLButtonElement;
    this.retireBtn = this.q('#retire-btn') as HTMLButtonElement;
    this.cardsOverlay = this.q('#cards-overlay');
    this.cardsRow = this.q('#cards-row');
    this.deathOverlay = this.q('#death-overlay');
    this.offlineOverlay = this.q('#offline-overlay');
    this.banner = this.q('#banner');
    this.toasts = this.q('#toasts');
    this.labPanel = this.q('#lab');
    this.labSpoils = this.q('#lab-spoils');
    this.techList = this.q('#tech-list');
    this.artifactList = this.q('#artifact-list');
    this.synergyStrip = this.q('#synergies');
    this.buffStrip = this.q('#buffs');

    // resource rows
    const grid = this.q('#resources');
    for (const id of RESOURCE_ORDER) {
      const def = RESOURCES[id];
      const ico = document.createElement('div');
      ico.className = 'ico';
      ico.textContent = def.icon;
      const amt = document.createElement('div');
      amt.className = 'amt';
      amt.textContent = '0';
      const rate = document.createElement('div');
      rate.className = 'rate';
      rate.textContent = '0/s';
      // Wrap each row in a .res element (display: contents) so the scoped CSS
      // applies while the three cells stay direct participants of the grid.
      const row = document.createElement('div');
      row.className = 'res';
      row.append(ico, amt, rate);
      grid.appendChild(row);
      this.resAmt[id] = amt;
      this.resRate[id] = rate;
    }

    this.muteBtn.textContent = initialMuted ? '🔇' : '🔊';
    this.muteBtn.addEventListener('click', () => {
      const muted = this.ctrl.onToggleMute();
      this.muteBtn.textContent = muted ? '🔇' : '🔊';
    });

    // Lab toggle + close + static tech rows (artifact rows rebuild as they drop).
    this.q('#lab-btn').addEventListener('click', () => {
      this.labPanel.classList.toggle('show');
    });
    this.q('#lab-close').addEventListener('click', () => {
      this.labPanel.classList.remove('show');
    });
    this.ascendBtn.addEventListener('click', () => this.ctrl.onPrestige());
    this.waveNowBtn.addEventListener('click', () => this.ctrl.onCallWave());
    // Retiring ends the run: require a second tap within 3.5s to confirm.
    this.retireBtn.addEventListener('click', () => {
      const now = performance.now();
      if (now - this.retireArmedAt < 3500) {
        this.retireArmedAt = 0;
        this.ctrl.onRetire();
      } else {
        this.retireArmedAt = now;
        // Cached writer: a raw textContent write would desync setText's __t
        // cache, so the expiry relabel in updateWavePanel would be skipped and
        // the scary confirm label would stick forever.
        this.setText(this.retireBtn, '⚠️ ' + t('retire.confirm'));
      }
    });

    // Opening story button (the overlay itself is shown by showIntroIfNew(),
    // which main calls after login so it never covers the login screen).
    this.q('#intro-btn').addEventListener('click', () => {
      this.q('#intro-overlay').classList.remove('show');
      try {
        localStorage.setItem('gokturk.introSeen', '1');
      } catch {
        /* ignore */
      }
      this.startTutorial();
    });

    this.buildTechRows();

    // Account panel (login is driven separately by main via showLogin()).
    this.q('#account-btn').addEventListener('click', () =>
      this.q('#account-overlay').classList.add('show'),
    );
    this.q('#account-close').addEventListener('click', () =>
      this.q('#account-overlay').classList.remove('show'),
    );
    this.q('#account-logout').addEventListener('click', () => {
      clearToken();
      location.reload();
    });
    this.q('#account-signin').addEventListener('click', () => {
      this.q('#account-overlay').classList.remove('show');
      this.ctrl.onSignIn();
    });
    this.q('#feedback-send').addEventListener('click', () => void this.sendFeedback());

    // Hard reset (two-step confirm).
    const resetBtn = this.q('#reset-btn');
    const resetConfirm = this.q('#reset-confirm');
    resetBtn.addEventListener('click', () => {
      resetConfirm.style.display = 'block';
      resetBtn.style.display = 'none';
    });
    this.q('#reset-no').addEventListener('click', () => {
      resetConfirm.style.display = 'none';
      resetBtn.style.display = '';
    });
    this.q('#reset-yes').addEventListener('click', () => {
      resetConfirm.style.display = 'none';
      resetBtn.style.display = '';
      this.q('#account-overlay').classList.remove('show');
      this.ctrl.onHardReset();
    });

    // What's-new overlay + feedback nudge.
    this.q('#news-btn').addEventListener('click', () => {
      this.q('#news-overlay').classList.remove('show');
      this.startTutorial();
    });
    // The version footer opens the full changelog any time.
    this.q('#version').addEventListener('click', () => this.showWhatsNew(NEWS, true));
    this.q('#nudge-btn').addEventListener('click', () => {
      this.q('#nudge').classList.remove('show');
      this.q('#account-overlay').classList.add('show');
    });
    this.q('#nudge-x').addEventListener('click', () => this.q('#nudge').classList.remove('show'));

    // Tutorial controls.
    this.q('#tut-next').addEventListener('click', () => this.showTutStep(this.tutStep + 1));
    this.q('#tut-skip').addEventListener('click', () => this.endTutorial());
    this.q('#tut-replay').addEventListener('click', () => {
      this.q('#account-overlay').classList.remove('show');
      this.startTutorial(true);
    });

    // Language switch: persist and reload so the whole UI rebuilds in the new language.
    this.root.querySelectorAll<HTMLElement>('.lang-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lang = btn.dataset.lang as Lang;
        if (lang && lang !== getLang()) {
          setLang(lang);
          location.reload();
        }
      });
    });

    this.bindBusForFeedback();
  }

  /** Shows the login overlay and resolves once the player verifies a code. */
  showLogin(): Promise<VerifyResult> {
    return new Promise((resolve) => {
      const overlay = this.q('#login-overlay');
      const emailStep = this.q('#login-step-email');
      const codeStep = this.q('#login-step-code');
      const emailInput = this.q<HTMLInputElement>('#login-email');
      const codeInput = this.q<HTMLInputElement>('#login-code');
      const msg = this.q('#login-msg');
      let email = '';

      overlay.classList.add('show');
      emailStep.style.display = 'flex';
      codeStep.style.display = 'none';
      void this.mountGoogleSignIn(resolve, msg, overlay);

      this.q('#login-send').onclick = async () => {
        email = emailInput.value.trim().toLowerCase();
        msg.textContent = t('login.sending');
        try {
          const r = await api.requestCode(email);
          emailStep.style.display = 'none';
          codeStep.style.display = 'flex';
          msg.textContent = r.devCode ? `dev code: ${r.devCode}` : t('login.codeSent');
          codeInput.value = '';
          codeInput.focus();
        } catch (e) {
          msg.textContent = (e as Error).message || t('login.errGeneric');
        }
      };

      this.q('#login-verify').onclick = async () => {
        const code = codeInput.value.trim();
        msg.textContent = t('login.verifying');
        try {
          const res = await api.verify(email, code);
          setToken(res.token);
          overlay.classList.remove('show');
          msg.textContent = '';
          resolve(res);
        } catch (e) {
          msg.textContent = (e as Error).message;
        }
      };

      this.q('#login-back').onclick = () => {
        emailStep.style.display = 'flex';
        codeStep.style.display = 'none';
        msg.textContent = '';
      };

      // Play now, sign in later. An empty token is the guest marker: main skips
      // the cloud adoption and stores nothing, so the run lives in localStorage
      // exactly like a logged-in one until the player chooses to link it. The
      // account is offered again from the 👤 panel, with the local save intact.
      this.q('#login-guest').onclick = () => {
        overlay.classList.remove('show');
        msg.textContent = '';
        track('guest_start');
        resolve({ token: '', email: '', name: null, save: null });
      };
    });
  }

  /** Renders the Google Sign-In button when the backend has a client id
   *  configured. The credential is verified server-side; success resolves the
   *  login promise exactly like the email-code flow. Silent no-op otherwise. */
  private async mountGoogleSignIn(
    resolve: (r: VerifyResult) => void,
    msg: HTMLElement,
    overlay: HTMLElement,
  ): Promise<void> {
    try {
      const cfg = await api.authConfig();
      if (!cfg.google || !cfg.googleClientId) return;
      await loadGsi();
      const gsi = (window as unknown as { google?: GsiGlobal }).google;
      if (!gsi) return;
      this.q('#login-google').style.display = 'flex';
      gsi.accounts.id.initialize({
        client_id: cfg.googleClientId,
        callback: (resp) => {
          msg.textContent = t('login.verifying');
          api
            .google(resp.credential)
            .then((res) => {
              setToken(res.token);
              overlay.classList.remove('show');
              msg.textContent = '';
              resolve(res);
            })
            .catch((e) => {
              msg.textContent = (e as Error).message || t('err.google_failed');
            });
        },
      });
      gsi.accounts.id.renderButton(this.q('#google-slot'), {
        theme: 'filled_black',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        locale: getLang(),
        width: 260,
      });
    } catch {
      /* backend unreachable or GIS blocked: the email flow still works */
    }
  }

  /** Shows who is signed in. A guest gets the sign-in offer in the account
   *  panel instead of a logout button, so the "later" in "sign in later" is
   *  actually reachable. */
  setAccount(email: string, guest = false): void {
    this.q('#account-email').textContent = guest ? t('account.guest') : email;
    this.q('#account-signin').style.display = guest ? '' : 'none';
    this.q('#account-logout').style.display = guest ? 'none' : '';
  }

  /** Shows the one-time opening story (after login). */
  showIntroIfNew(): void {
    let seen = false;
    try {
      seen = localStorage.getItem('gokturk.introSeen') === '1';
    } catch {
      /* ignore */
    }
    if (!seen) this.q('#intro-overlay').classList.add('show');
  }

  /** Shows the "what's new" overlay. `full` (footer click) shows the whole
   *  changelog; otherwise it is the "since your last visit" login panel. */
  showWhatsNew(entries: NewsEntry[], full = false): void {
    if (!entries.length) return;
    this.q('#news-sub').textContent = full ? t('news.all') : t('news.sub', { version: VERSION });
    const list = this.q('#news-list');
    list.innerHTML = '';
    for (const e of entries) {
      const head = document.createElement('div');
      head.className = 'news-ver';
      head.textContent = 'v' + e.version;
      list.appendChild(head);
      // Turkish reads the master copy; every other locale gets the English copy
      // when it exists (older entries fall back to the Turkish master).
      const lines = getLang() === 'tr' ? e.items : (e.itemsEn ?? e.items);
      for (const item of lines) {
        const it = document.createElement('div');
        it.className = 'news-item';
        it.textContent = item;
        list.appendChild(it);
      }
    }
    this.q('#news-overlay').classList.add('show');
  }

  private showNudge(text: string): void {
    this.q('#nudge-text').textContent = text;
    this.q('#nudge').classList.add('show');
    this.nudgeTimer = 9;
  }

  // ---- first-run tutorial (spotlight coachmarks) ----

  /** Starts the tutorial. Skips if already seen unless `force` is set. */
  startTutorial(force = false): void {
    if (!force) {
      try {
        if (localStorage.getItem('gokturk.tutorialSeen') === '1') return;
      } catch {
        /* ignore */
      }
    }
    this.tutorialOn = true;
    track('tutorial_start');
    this.q('#tutorial').classList.add('show');
    this.showTutStep(0);
  }

  private showTutStep(i: number): void {
    if (i < 0 || i >= this.tutSteps.length) {
      this.endTutorial('done');
      return;
    }
    this.tutStep = i;
    const step = this.tutSteps[i];
    this.q('#tut-title').textContent = t(step.key + '.title');
    this.q('#tut-text').textContent = t(step.key + '.text');
    this.q('#tut-count').textContent = `${i + 1} / ${this.tutSteps.length}`;
    this.q('#tut-next').textContent = i === this.tutSteps.length - 1 ? t('common.finish') : t('common.next');

    // The pulsing tap-hint only shows on the core-action (no-target) step.
    this.q('#tut-tap').classList.toggle('on', step.sel === null);

    const ring = this.q('#tut-ring');
    const tip = this.q('#tut-tip');
    tip.style.transform = '';
    const target = step.sel ? (document.querySelector(step.sel) as HTMLElement | null) : null;
    if (target) {
      const r = target.getBoundingClientRect();
      const pad = 8;
      ring.style.display = 'block';
      ring.style.left = `${r.left - pad}px`;
      ring.style.top = `${r.top - pad}px`;
      ring.style.width = `${r.width + pad * 2}px`;
      ring.style.height = `${r.height + pad * 2}px`;
      const tw = tip.offsetWidth;
      const th = tip.offsetHeight;
      let left = r.left + r.width / 2 - tw / 2;
      left = Math.max(8, Math.min(window.innerWidth - tw - 8, left));
      const top =
        r.top < window.innerHeight / 2
          ? Math.min(window.innerHeight - th - 8, r.bottom + pad + 6)
          : Math.max(8, r.top - pad - 6 - th);
      tip.style.left = `${left}px`;
      tip.style.top = `${top}px`;
    } else {
      // Core-action step: keep the card up top so the map + tap-hint stay visible.
      ring.style.display = 'none';
      const tw = tip.offsetWidth;
      tip.style.left = `${Math.max(8, window.innerWidth / 2 - tw / 2)}px`;
      tip.style.top = '14%';
    }
  }

  /** Called when the player actually harvests; advances past the core-action step
   *  so the tutorial teaches by doing on touch. */
  private onTutorialHarvest(): void {
    if (this.tutorialOn && this.tutStep === 0) this.showTutStep(1);
  }

  private endTutorial(reason: 'done' | 'skip' = 'skip'): void {
    if (this.tutorialOn) track(reason === 'done' ? 'tutorial_done' : 'tutorial_skip');
    this.tutorialOn = false;
    this.q('#tut-tap').classList.remove('on');
    this.q('#tutorial').classList.remove('show');
    try {
      localStorage.setItem('gokturk.tutorialSeen', '1');
    } catch {
      /* ignore */
    }
  }

  private async sendFeedback(): Promise<void> {
    const ta = this.q<HTMLTextAreaElement>('#feedback-text');
    const fmsg = this.q('#feedback-msg');
    const text = ta.value.trim();
    if (!text) {
      fmsg.textContent = t('account.feedbackEmpty');
      return;
    }
    fmsg.textContent = t('login.sending');
    try {
      const s = this.lastState;
      await api.feedback(text, {
        version: VERSION,
        planet: s?.meta.planet ?? 0,
        bestWave: s?.meta.bestWave ?? 0,
        age: s?.run.age ?? 0,
      });
      fmsg.textContent = t('account.feedbackThanks');
      ta.value = '';
    } catch (e) {
      fmsg.textContent = (e as Error).message || t('account.feedbackError');
    }
  }

  /** Builds the (static) technology rows once and keeps button refs to update. */
  private buildTechRows(): void {
    this.techList.innerHTML = '';
    this.techBuyBtns = {};
    for (const tech of TECHS) {
      const row = document.createElement('div');
      row.className = 'lab-row';
      row.innerHTML = `
        <div class="li">${tech.icon}</div>
        <div class="lb"><div class="ln">${tc('tech.' + tech.id + '.name', tech.name)}</div><div class="ld">${tc('tech.' + tech.id + '.desc', tech.desc)}</div></div>`;
      const btn = document.createElement('button');
      btn.className = 'lbtn';
      btn.addEventListener('click', () => this.ctrl.onBuyTech(tech.id));
      row.appendChild(btn);
      this.techList.appendChild(row);
      this.techBuyBtns[tech.id] = btn;
    }
  }

  private skeleton(): string {
    return `
      <div id="identity" class="panel interactive">
        <div class="title">${GAME_NAME} <span class="star">★</span></div>
        <div class="sub">${t('intro.tagline')}</div>
        <!-- Left empty on purpose: literal English here flashed on every load
             for Turkish players (the core audience) until the first update tick. -->
        <div class="age" id="age-label"></div>
        <div style="font-size:11px;color:var(--ink-dim);margin-top:2px" id="tier-name"></div>
        <div style="height:5px;border-radius:3px;background:rgba(255,255,255,.08);margin-top:6px;overflow:hidden">
          <div id="progress-fill" style="height:100%;width:0%;background:linear-gradient(90deg,var(--sky),var(--gold));transition:width .2s ease"></div>
        </div>
        <div id="id-stats">
          <span class="st"><span>${t('hud.population')}</span> <b id="stat-pop">0</b></span>
          <span class="st"><span>${t('hud.buildings')}</span> <b id="stat-bld">0</b></span>
          <span class="auto off" id="stat-auto"></span>
        </div>
        <div id="next-build">
          <div class="nb-head">${t('hud.nextBuilding')}: <b id="nb-name">-</b></div>
          <div id="nb-bars"></div>
        </div>
        <div id="synergies"></div>
        <div id="buffs"></div>
      </div>

      <div id="meta">
        <div class="chip panel"><span class="k">${t('hud.spoils')}</span><span class="v spoils" id="chip-spoils">0</span></div>
        <div class="chip panel"><span class="k">${t('hud.skillPts')}</span><span class="v sp" id="chip-sp">0</span></div>
        <div class="chip panel"><span class="k">${t('hud.cosmic')}</span><span class="v cosmic" id="chip-cosmic">0</span></div>
        <button class="iconbtn" id="lab-btn" title="${t('lab.title')}">🔬</button>
        <button class="iconbtn" id="account-btn" title="${t('account.btnTitle')}">👤</button>
        <button class="iconbtn" id="mute-btn" title="${t('ui.soundTitle')}">🔊</button>
      </div>

      <div id="ascend"><button id="ascend-btn">🚀 ${t('hud.ascend')}</button></div>

      <div id="resources" class="panel interactive"></div>


      <div id="wave" class="panel interactive">
        <div class="row">
          <span class="label" id="wave-label">${t('wave.next')}</span>
          <span class="count" id="wave-count">--</span>
        </div>
        <div class="matchup">
          <span>${t('wave.yourPower')} <span class="def" id="wave-def">0</span></span>
          <span>${t('wave.enemyPower')} <span class="thr" id="wave-threat">0</span> <span class="wv" id="wave-verdict"></span></span>
        </div>
        <div id="wave-boss" style="margin-top:5px;font-size:11px;text-align:center"></div>
        <div id="wave-conquest" style="margin-top:4px;font-size:11px;color:var(--ink-dim);text-align:center"></div>
        <button class="wavebtn" id="wave-now-btn">⚔️ ${t('wave.sendNow')}</button>
        <button class="wavebtn danger" id="retire-btn"></button>
      </div>

      <div id="banner"><div class="big" id="banner-big"></div><div class="small" id="banner-small"></div></div>
      <div id="toasts"></div>
      <div id="nudge"><span id="nudge-text"></span><button id="nudge-btn">${t('nudge.cta')}</button><button class="nudge-x" id="nudge-x">✕</button></div>

      <div id="tutorial">
        <div id="tut-ring"></div>
        <div id="tut-tap"><span class="finger">👆</span></div>
        <div id="tut-tip">
          <h4 id="tut-title"></h4>
          <p id="tut-text"></p>
          <div id="tut-foot">
            <span id="tut-count"></span>
            <div id="tut-btns">
              <button id="tut-skip">${t('common.skip')}</button>
              <button id="tut-next">${t('common.next')}</button>
            </div>
          </div>
        </div>
      </div>

      <div id="cards-overlay" class="overlay">
        <h2>${t('cards.title')}</h2>
        <div class="lede">${t('cards.sub')}</div>
        <div id="cards-row"></div>
      </div>

      <div id="death-overlay" class="overlay">
        <h2 style="color:var(--bad)">${t('death.title')}</h2>
        <div id="death-payday">+<span id="death-payday-n">0</span> ⭐</div>
        <div class="lede" id="death-lede"></div>
        <div id="death-stats"></div>
        <div id="skill-points">${t('death.skillPoints')}: <b id="death-sp">0</b></div>
        <div id="skills-tree">
          <div id="skills-canvas-wrap"><div id="skills-canvas"></div></div>
          <div id="skill-detail"></div>
        </div>
        <div style="display:flex;gap:12px;flex-wrap:wrap;justify-content:center">
          <button class="bigbtn" id="death-ascend-btn" style="display:none;background:linear-gradient(135deg,#ffd24a,#b56bff)">🚀 ${t('hud.ascend')}</button>
          <button class="bigbtn" id="restart-btn">${t('death.restart')}</button>
        </div>
      </div>

      <div id="offline-overlay" class="overlay">
        <h2>${t('offline.title')}</h2>
        <div class="lede" id="offline-sub"></div>
        <div id="offline-list"></div>
        <button class="bigbtn" id="offline-btn">${t('common.continue')}</button>
      </div>

      <div id="login-overlay" class="overlay">
        <h2>${GAME_NAME}</h2>
        <div class="lede" style="max-width:360px;text-align:center">${t('login.sub')}</div>
        <div id="login-step-email" style="display:flex;flex-direction:column;align-items:center">
          <input id="login-email" class="field interactive" type="email" placeholder="${t('login.emailPlaceholder')}" autocomplete="email" />
          <button class="bigbtn" id="login-send">${t('login.sendCode')}</button>
        </div>
        <div id="login-step-code" style="display:none;flex-direction:column;align-items:center">
          <input id="login-code" class="field interactive" inputmode="numeric" maxlength="6" placeholder="${t('login.codePlaceholder')}" />
          <button class="bigbtn" id="login-verify">${t('login.verify')}</button>
          <button class="linkbtn interactive" id="login-back">${t('common.back')}</button>
        </div>
        <div id="login-google" style="display:none;flex-direction:column;align-items:center;gap:10px;margin-top:14px">
          <div id="login-or" style="font-size:12px;color:var(--ink-dim);text-transform:uppercase;letter-spacing:1px">${t('login.or')}</div>
          <div id="google-slot" class="interactive"></div>
        </div>
        <div id="login-msg" class="login-msg"></div>
        <button class="linkbtn interactive" id="login-guest">${t('login.guest')}</button>
      </div>

      <div id="account-overlay" class="overlay">
        <h2>${t('account.title')}</h2>
        <div class="lede" id="account-email"></div>
        <button class="bigbtn interactive" id="account-signin" style="display:none">${t('account.signIn')}</button>

        <div class="lab-sec">${t('account.language')}</div>
        <div id="lang-row">${LANGS.map(
          (l) =>
            `<button class="lang-btn interactive${l.code === getLang() ? ' on' : ''}" data-lang="${l.code}">${l.label}</button>`,
        ).join('')}</div>

        <div class="lab-sec">${t('account.feedbackLabel')}</div>
        <textarea id="feedback-text" class="field interactive" rows="4" placeholder="${t('account.feedbackPlaceholder')}"></textarea>
        <button class="bigbtn" id="feedback-send">${t('common.send')}</button>
        <div id="feedback-msg" class="login-msg"></div>

        <div class="lab-sec" style="color:var(--bad)">${t('account.dangerZone')}</div>
        <button class="linkbtn interactive" id="reset-btn" style="color:var(--bad)">${t('account.resetBtn')}</button>
        <div id="reset-confirm" style="display:none;text-align:center">
          <div style="color:var(--bad);font-size:13px;margin:6px 0 10px;max-width:340px">${t('account.resetConfirm')}</div>
          <button class="bigbtn interactive" id="reset-yes" style="background:linear-gradient(135deg,#ff5a5a,#c62828);color:#fff">${t('account.resetYes')}</button>
          <button class="linkbtn interactive" id="reset-no">${t('account.resetNo')}</button>
        </div>

        <button class="linkbtn interactive" id="tut-replay">${t('account.replayTutorial')}</button>
        <button class="linkbtn interactive" id="account-close">${t('common.close')}</button>
        <button class="linkbtn interactive" id="account-logout">${t('account.logout')}</button>
      </div>

      <div id="intro-overlay" class="overlay">
        <h2 style="font-size:46px;letter-spacing:3px;margin-bottom:10px">${GAME_NAME}</h2>
        <div class="lede" style="max-width:540px;text-align:center;line-height:1.65;font-size:15px">${t('intro.story')}</div>
        <button class="bigbtn" id="intro-btn">${t('intro.begin')}</button>
      </div>

      <div id="news-overlay" class="overlay">
        <h2>${t('news.title')}</h2>
        <div class="lede" id="news-sub"></div>
        <div id="news-list"></div>
        <button class="bigbtn" id="news-btn">${t('common.continue')}</button>
      </div>

      <div id="lab">
        <div class="lab-head">
          <h3>🔬 ${t('lab.title')}</h3>
          <span class="sp">${t('lab.spoilsWord')}: <b id="lab-spoils">0</b> 💎</span>
          <button class="iconbtn" id="lab-close" title="${t('common.close')}">✕</button>
        </div>
        <div class="lab-body">
          <div class="lab-sec">${t('lab.techs')}</div>
          <div id="tech-list"></div>
          <div class="lab-sec">${t('lab.artifacts')}</div>
          <div id="artifact-list"></div>
        </div>
      </div>

      <button id="version" class="interactive" title="${GAME_NAME} v${VERSION}"><span class="vico">📋</span><span>v${VERSION}</span></button>
    `;
  }

  private q<T extends HTMLElement = HTMLElement>(sel: string): T {
    const el = this.root.querySelector<T>(sel);
    if (!el) throw new Error(`UI element not found: ${sel}`);
    return el;
  }

  // Cached DOM writers: the HUD ticks at ~10Hz and most values are unchanged
  // between ticks; writing identical textContent still replaces the text node
  // and keeps style/layout dirty, which mid-range phones pay for.
  private setText(el: HTMLElement, v: string): void {
    const rec = el as HTMLElement & { __t?: string };
    if (rec.__t !== v) {
      rec.__t = v;
      el.textContent = v;
    }
  }

  private setWidth(el: HTMLElement, v: string): void {
    const rec = el as HTMLElement & { __w?: string };
    if (rec.__w !== v) {
      rec.__w = v;
      el.style.width = v;
    }
  }

  // ---- per-frame HUD update ----

  update(state: GameState): void {
    this.lastState = state;
    const run = state.run;

    this.setText(this.ageEl, `${run.age + 1}. ${ageName(run.age)}`);
    this.setText(this.tierNameEl, `${planetName(state.meta.planet)} · ${tierName(run.settlementTier)}`);

    // age progress bar
    if (run.age >= BALANCE.ages.maxAge) {
      this.setWidth(this.progressFill, '100%');
    } else {
      const need = ageThreshold(run.age);
      const pct = Math.max(0, Math.min(100, (run.progress / need) * 100));
      this.setWidth(this.progressFill, pct.toFixed(1) + '%');
    }

    this.setText(this.spoilsEl, fmt(run.spoils));
    this.setText(this.spEl, fmtInt(state.meta.skillPoints));
    this.setText(this.cosmicEl, fmtInt(state.meta.cosmic));

    // Ascend (planetary prestige) call to action when the planet is conquered.
    if (run.conquered) {
      const gain = cosmicGain(state.meta.planet, run.age);
      this.setText(
        this.ascendBtn,
        t('death.ascendTo', { planet: planetName(state.meta.planet + 1), gain: fmtInt(gain) }),
      );
      this.ascendEl.classList.add('show');
    } else {
      this.ascendEl.classList.remove('show');
    }

    const income = computeIncome(state);
    for (const id of RESOURCE_ORDER) {
      this.setText(this.resAmt[id], fmt(run.resources[id]));
      const r = income[id];
      const rateEl = this.resRate[id];
      this.setText(rateEl, r > 0 ? '+' + fmtRate(r) : '0/s');
      rateEl.classList.toggle('pos', r > 0);
    }

    this.setText(this.popEl, fmtInt(run.population));
    this.setText(this.bldEl, fmtInt(run.buildingCount));
    if (run.autoUnlocked) {
      this.setText(this.autoEl, t('hud.autoOn'));
      this.autoEl.classList.remove('off');
    } else {
      const g = run.buildings.lumberCamp + run.buildings.quarry + run.buildings.mine;
      const need = BALANCE.autoCollect.unlockGatherers;
      this.setText(this.autoEl, t('hud.autoProgress', { have: Math.min(g, need), need }));
      this.autoEl.classList.add('off');
    }

    this.updateNextBuild(state);
    this.updateWavePanel(state);
    this.updateSynergies(state);
    this.updateBuffs(state);
    this.updateLab(state);
  }

  /** "Saving for the next building" indicator: name + a progress bar per resource
   *  it still needs. Rebuilds the rows only when the target/resource set changes. */
  private updateNextBuild(state: GameState): void {
    const plan = nextPlannedBuilding(state);
    if (!plan) {
      this.setText(this.nbName, '-'); // cached writer: keeps __t in sync (raw writes desync it)
      if (this.nbSig !== 'none') {
        this.nbSig = 'none';
        this.nbBars.innerHTML = '';
        this.nbFills = {};
        this.nbNums = {};
      }
      return;
    }
    if (plan.gatedByPop) {
      this.setText(this.nbName, t('hud.needPop'));
      if (this.nbSig !== 'pop') {
        this.nbSig = 'pop';
        this.nbBars.innerHTML = '';
        this.nbFills = {};
        this.nbNums = {};
      }
      return;
    }

    const keys = Object.keys(plan.cost) as ResourceId[];
    this.setText(this.nbName, tc('building.' + plan.id + '.name', BUILDINGS[plan.id].name));
    const sig = plan.id + ':' + keys.join(',');
    if (sig !== this.nbSig) {
      this.nbSig = sig;
      this.nbBars.innerHTML = '';
      this.nbFills = {};
      this.nbNums = {};
      for (const res of keys) {
        const row = document.createElement('div');
        row.className = 'nb-bar';
        const ico = document.createElement('span');
        ico.className = 'ico';
        ico.textContent = RESOURCES[res].icon;
        const track = document.createElement('span');
        track.className = 'track';
        const fill = document.createElement('span');
        fill.className = 'fill';
        track.appendChild(fill);
        const num = document.createElement('span');
        num.className = 'num';
        row.append(ico, track, num);
        this.nbBars.appendChild(row);
        this.nbFills[res] = fill;
        this.nbNums[res] = num;
      }
    }
    for (const res of keys) {
      const need = plan.cost[res] ?? 0;
      const have = state.run.resources[res];
      const pct = need > 0 ? Math.min(100, (have / need) * 100) : 100;
      const fill = this.nbFills[res];
      const num = this.nbNums[res];
      if (fill) this.setWidth(fill, pct.toFixed(0) + '%');
      if (num) this.setText(num, `${fmt(Math.min(have, need))}/${fmt(need)}`);
      this.nbFills[res]?.parentElement?.parentElement?.classList.toggle('done', have >= need);
    }
  }

  // ---- synergies, buffs, lab (Phase 2) ----

  private updateSynergies(state: GameState): void {
    const active = activeSynergies(state.run.pickedCards);
    const sig = active.map((s) => s.id).join(',');
    if (sig === this.lastSynergySig) return;
    this.lastSynergySig = sig;
    this.synergyStrip.innerHTML = '';
    for (const syn of active) {
      const chip = document.createElement('span');
      chip.className = 'syn';
      chip.textContent = `${syn.icon} ${tc('synergy.' + syn.id + '.name', syn.name)}`;
      chip.title = tc('synergy.' + syn.id + '.desc', syn.desc);
      this.synergyStrip.appendChild(chip);
    }
  }

  private updateBuffs(state: GameState): void {
    const buffs = state.run.buffs;
    // Rebuild chips only when the set of buff ids changes; the countdown text
    // updates in place each frame (buff ids are unique, addBuff refreshes).
    const sig = buffs.map((b) => b.id).join(',');
    if (sig !== this.buffSig) {
      this.buffSig = sig;
      this.buffStrip.innerHTML = '';
      this.buffChips = {};
      for (const buff of buffs) {
        const chip = document.createElement('div');
        chip.className = 'buff';
        this.buffStrip.appendChild(chip);
        this.buffChips[buff.id] = chip;
      }
    }
    for (const buff of buffs) {
      const chip = this.buffChips[buff.id];
      if (!chip) continue;
      const remaining = Math.max(0, buff.expiresAt - state.gameTime);
      this.setText(chip, `${buff.icon} ${tc('tech.' + buff.id + '.name', buff.label)} ${Math.ceil(remaining)}s`);
    }
  }

  private updateLab(state: GameState): void {
    if (!this.labPanel.classList.contains('show')) return; // offscreen: skip the refresh

    this.setText(this.labSpoils, fmt(state.run.spoils));

    // Technology buttons (cost / owned / affordability).
    for (const tech of TECHS) {
      const btn = this.techBuyBtns[tech.id];
      if (!btn) continue;
      const owned = state.run.techs[tech.id] ?? 0;
      const row = btn.parentElement;
      if (!tech.repeatable && owned >= 1) {
        this.setText(btn, t('lab.owned'));
        btn.disabled = true;
        row?.classList.add('active');
      } else {
        const cost = techCost(tech, owned);
        this.setText(btn, `${fmt(cost)} 💎${tech.repeatable && owned > 0 ? ` (x${owned})` : ''}`);
        btn.disabled = state.run.spoils < cost;
        row?.classList.remove('active');
      }
    }

    // Artifact list rebuilds when the held/activated set OR the age changes
    // (activation costs scale with the current age, so the cost line must too).
    const sig = state.run.age + '|' + state.run.artifacts.map((a) => `${a.id}:${a.activated ? 1 : 0}`).join(',');
    if (sig !== this.artifactSig) {
      this.artifactSig = sig;
      this.renderArtifacts(state);
    }
    // Update activate-button affordability every frame.
    for (const art of state.run.artifacts) {
      if (art.activated) continue;
      const btn = this.artifactBtns[art.id];
      const def = ARTIFACT_BY_ID[art.id];
      if (!btn || !def) continue;
      const can = (Object.entries(activationCostAt(def, state.run.age)) as [string, number][]).every(
        ([res, amt]) => state.run.resources[res as keyof typeof state.run.resources] >= amt,
      );
      btn.disabled = !can;
    }
  }

  private renderArtifacts(state: GameState): void {
    this.artifactList.innerHTML = '';
    this.artifactBtns = {};
    if (state.run.artifacts.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'lab-empty';
      empty.textContent = t('lab.artifactsHint');
      this.artifactList.appendChild(empty);
      return;
    }
    for (const art of state.run.artifacts) {
      const def = ARTIFACT_BY_ID[art.id];
      if (!def) continue;
      const costStr = (Object.entries(activationCostAt(def, state.run.age)) as [string, number][])
        .map(([res, amt]) => `${fmt(amt)} ${RESOURCES[res as keyof typeof RESOURCES].icon}`)
        .join('  ');
      const row = document.createElement('div');
      row.className = 'lab-row' + (art.activated ? ' active' : '');
      row.innerHTML = `
        <div class="li">${def.icon}</div>
        <div class="lb"><div class="ln">${tc('artifact.' + art.id + '.name', def.name)}</div><div class="ld">${tc('artifact.' + art.id + '.desc', def.desc)}</div>
          ${art.activated ? `<div class="lown">${t('lab.active')}</div>` : `<div class="lown">${t('lab.cost')}: ${costStr}</div>`}
        </div>`;
      if (!art.activated) {
        const btn = document.createElement('button');
        btn.className = 'lbtn';
        btn.textContent = t('lab.activate');
        btn.addEventListener('click', () => this.ctrl.onActivateArtifact(art.id));
        row.appendChild(btn);
        this.artifactBtns[art.id] = btn;
      }
      this.artifactList.appendChild(row);
    }
  }

  private updateWavePanel(state: GameState): void {
    const run = state.run;
    const nextWave = run.wave + 1;
    // Whether the imminent wave is the era gate boss: the latched flag during a
    // telegraph, otherwise "is the boss standing at the gate?"
    const boss = run.combatResolveAt > 0 ? run.combatIsBoss : run.gatePending;

    // The gate line replaces the old boss schedule: it shows the era boss
    // gathering at the gate (progress toward the age threshold), turns red and
    // bold once the boss is actually summoned, and reads "final age" at the cap.
    const bossEl = this.waveBoss;
    const atMaxAge = run.age >= BALANCE.ages.maxAge;
    if (atMaxAge) {
      this.setText(bossEl, '👑 ' + t('gate.final'));
    } else if (run.gatePending) {
      this.setText(bossEl, '💀 ' + t('gate.atGate', { boss: gateBossName(run.age) }));
    } else if (run.age < BALANCE.ages.firstGateAge) {
      // Early ages advance free (no boss): a neutral progress line, no skull.
      const pct = Math.min(99, Math.floor((run.progress / ageThreshold(run.age)) * 100));
      this.setText(bossEl, '⭐ ' + t('gate.freeProgress', { pct }));
    } else {
      const pct = Math.min(99, Math.floor((run.progress / ageThreshold(run.age)) * 100));
      this.setText(bossEl, '💀 ' + t('gate.progress', { boss: gateBossName(run.age), pct }));
    }
    // classList.toggle no-ops when unchanged, so the 10Hz tick stays write-free.
    bossEl.classList.toggle('gate-alert', !atMaxAge && run.gatePending);

    if (run.combatResolveAt > 0) {
      this.setText(this.waveLabel, boss ? t('wave.bossIncoming') : t('wave.incoming'));
      this.setText(this.waveCount, t('wave.now'));
      this.waveCount.classList.add('warn');
    } else {
      this.setText(this.waveLabel, boss ? t('wave.bossN', { n: nextWave }) : t('wave.waveN', { n: nextWave }));
      const remaining = Math.max(0, run.nextWaveAt - state.gameTime);
      this.setText(this.waveCount, fmtDuration(remaining));
      this.waveCount.classList.remove('warn');
    }

    // Combat legibility: show your power AND the incoming wave's threat with a
    // verdict, using the exact numbers the sim resolves on (win = def >= threat),
    // so a win or a loss reads as earned instead of an opaque coin flip.
    const def = settlementDefense(state);
    const threat = effectiveThreat(state, boss);
    this.setText(this.waveDef, fmt(def));
    this.setText(this.waveThreat, fmt(threat));
    const ratio = threat > 0 ? def / threat : 2;
    let verdict = '✓';
    let vClass = 'wv win';
    if (ratio < 1) {
      verdict = '✕';
      vClass = 'wv lose';
    } else if (ratio < 1.25) {
      verdict = '⚠';
      vClass = 'wv close';
    }
    this.setText(this.waveVerdict, verdict);
    if (this.waveVerdict.className !== vClass) this.waveVerdict.className = vClass;

    const planet = planetName(state.meta.planet);
    this.setText(
      this.waveConquest,
      state.run.conquered
        ? t('wave.conquered', { planet })
        // +1: the goal uses the same 1-indexed numbering as the HUD age label.
        : t('wave.conquer', { planet, goal: conquestAge(state.meta.planet) + 1 }),
    );

    // Send-now: only while waiting for the timer (hidden during the telegraph).
    // Guarded writes: this runs at 10Hz, so only touch the DOM on change.
    const nowDisplay = run.combatResolveAt > 0 ? 'none' : '';
    if (this.waveNowBtn.style.display !== nowDisplay) this.waveNowBtn.style.display = nowDisplay;

    // Retire: unlocked once this run matches the deepest age ever reached.
    // Hidden entirely on a player's very first run: "End civilization" was the
    // second-biggest button on screen for someone with two population who has
    // never been told what retiring is. It appears once they have died once and
    // the word means something.
    const retireDisplay = state.meta.totalRuns > 0 ? '' : 'none';
    if (this.retireBtn.style.display !== retireDisplay) {
      this.retireBtn.style.display = retireDisplay;
    }
    if (retireDisplay === 'none') return;

    const unlocked = canRetire(state);
    if (this.retireBtn.disabled !== !unlocked) this.retireBtn.disabled = !unlocked;
    if (this.retireArmedAt > 0 && performance.now() - this.retireArmedAt >= 3500) {
      this.retireArmedAt = 0; // confirm window expired: relabel below
    }
    if (this.retireArmedAt === 0) {
      this.setText(
        this.retireBtn,
        unlocked
          ? '🏛️ ' + t('retire.btn')
          : '🔒 ' + t('retire.lockedAt', { n: state.meta.bestAge + 1 }),
      );
    }
  }

  // ---- card picker ----

  showCards(state: GameState): void {
    const ids = state.pending.cardChoices;
    if (!ids) return;
    this.cardsRow.innerHTML = '';
    for (const id of ids) {
      const card = CARD_BY_ID[id];
      if (!card) continue;
      const el = document.createElement('button');
      el.className = `card interactive ${CAT_CLASS[card.category]}`;
      el.innerHTML = `
        <div class="cat">${t('cat.' + card.category)}</div>
        <div class="ic">${card.icon}</div>
        <div class="nm">${tc('card.' + card.id + '.name', card.name)}</div>
        <div class="ds">${tc('card.' + card.id + '.desc', card.desc)}</div>`;
      el.addEventListener('click', () => this.ctrl.onPickCard(id));
      this.cardsRow.appendChild(el);
    }
    this.cardsOverlay.classList.add('show');
  }

  hideCards(): void {
    this.cardsOverlay.classList.remove('show');
  }

  // ---- death / skill tree ----

  showDeath(state: GameState): void {
    // The "village overrun" banner is mid-animation when the run ends, and the
    // overlay behind it is only 72% opaque, so the big red defeat text bled
    // straight through the death stats and made the screen look broken. Clear
    // it as the overlay takes over.
    this.q('#banner').classList.remove('show');

    const age = state.run.age + 1; // 1-indexed like the HUD
    // run.wave is never incremented for the lost wave, so it equals waves HELD on
    // both the death and retire paths (matching the stats tiles below).
    const waves = state.run.wave;

    // Death is a PAYDAY, not a failure screen: the first death explains the
    // loop, every death counts its skill points up big and gold.
    // A gate-boss death gets its own lede so the player knows WHY they fell and
    // what to do differently. run.combatIsBoss is only cleared on a WIN, so on
    // the loss path (and after a reload of a dead save) it still holds the truth.
    const firstDeath = state.meta.totalRuns <= 1;
    const bossDeath = !state.pending.retired && state.run.combatIsBoss;
    this.q('#death-lede').textContent = state.pending.retired
      ? t('death.retireLede', { age, waves })
      : firstDeath
        ? t('death.firstLede')
        : bossDeath
          ? t('death.bossLede', { boss: gateBossName(state.run.age), age, waves })
          : t('death.lede', { age, waves });
    this.animatePayday(state.pending.deathSkillPoints);

    const stats = this.q('#death-stats');
    stats.innerHTML = `
      <div class="ds"><div class="v">${state.run.wave}</div><div class="k">${t('death.wavesHeld')}</div></div>
      <div class="ds"><div class="v">${state.run.age + 1}</div><div class="k">${t('death.ageReached')}</div></div>
      <div class="ds"><div class="v">+${state.pending.deathSkillPoints}</div><div class="k">${t('death.skillPoints')}</div></div>`;

    this.selectedSkill = null; // fresh open: auto-select the first buyable node
    this.renderSkills(state);

    const restart = this.q<HTMLButtonElement>('#restart-btn');
    restart.onclick = () => this.ctrl.onRestart();

    // If the planet was conquered before the fall, let the player still bank it.
    const ascend = this.q<HTMLButtonElement>('#death-ascend-btn');
    if (state.run.conquered) {
      const gain = cosmicGain(state.meta.planet, state.run.age);
      ascend.textContent = t('death.ascendTo', {
        planet: planetName(state.meta.planet + 1),
        gain: fmtInt(gain),
      });
      ascend.style.display = '';
      ascend.onclick = () => this.ctrl.onPrestige();
    } else {
      ascend.style.display = 'none';
    }

    this.deathOverlay.classList.add('show');
  }

  /** Counts the earned skill points up from 0 with a pop, so the payout is the
   *  emotional center of the death screen (research: death screens that hook are
   *  payday screens, not failure screens). */
  private animatePayday(earned: number): void {
    const el = this.q('#death-payday');
    const n = this.q('#death-payday-n');
    el.classList.remove('pop');
    void el.offsetWidth; // restart the CSS animation
    el.classList.add('pop');
    const durationMs = 900;
    const t0 = performance.now();
    const step = (now: number): void => {
      const p = Math.min(1, (now - t0) / durationMs);
      n.textContent = String(Math.round(earned * (1 - Math.pow(1 - p, 3))));
      if (p < 1 && this.deathOverlay.classList.contains('show')) requestAnimationFrame(step);
    };
    n.textContent = '0';
    requestAnimationFrame(step);
  }

  /** Layout of the skill tree: grid coords (x cols, y rows) + pillar per skill.
   *  The three roots sit on the top row and each branch grows downward, so the
   *  SVG links between a node and its prerequisite read as tree branches. */
  private readonly skillLayout: Record<
    string,
    { x: number; y: number; pillar: 'eco' | 'mil' | 'kno' }
  > = {
    'bountiful-start': { x: 1.5, y: 0, pillar: 'eco' },
    industrious: { x: 1, y: 1, pillar: 'eco' },
    'master-mason': { x: 0.35, y: 2, pillar: 'eco' },
    'swift-hands': { x: 1.4, y: 2, pillar: 'eco' },
    settlers: { x: 2.5, y: 1, pillar: 'eco' },
    'sharp-tools': { x: 2.6, y: 2, pillar: 'eco' },
    'wide-reach': { x: 2.6, y: 3, pillar: 'eco' },
    fortified: { x: 4.2, y: 0, pillar: 'mil' },
    'war-chest': { x: 4.2, y: 1, pillar: 'mil' },
    archivist: { x: 5.6, y: 0, pillar: 'kno' },
    'long-memory': { x: 5.6, y: 1, pillar: 'kno' },
  };

  /** Re-renders the skill tree (called on open and after each purchase). */
  renderSkills(state: GameState): void {
    this.q('#death-sp').textContent = fmtInt(state.meta.skillPoints);
    const canvas = this.q('#skills-canvas');
    canvas.innerHTML = '';
    const levels = state.meta.skillLevels;
    const CELL_W = 82;
    const CELL_H = 88;
    const NODE = 52;
    const PAD = 26;
    let maxX = 0;
    let maxY = 0;
    for (const def of SKILLS) {
      const p = this.skillLayout[def.id];
      if (!p) continue;
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    const W = PAD * 2 + maxX * CELL_W + NODE;
    const H = PAD * 2 + maxY * CELL_H + NODE;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    const cx = (p: { x: number }) => PAD + p.x * CELL_W + NODE / 2;
    const cy = (p: { y: number }) => PAD + p.y * CELL_H + NODE / 2;

    // Connector lines (an SVG layer sitting behind the nodes).
    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'skill-links');
    svg.setAttribute('width', `${W}`);
    svg.setAttribute('height', `${H}`);
    for (const def of SKILLS) {
      if (!def.requires) continue;
      const cp = this.skillLayout[def.id];
      const pp = this.skillLayout[def.requires.id];
      if (!cp || !pp) continue;
      const owned = (levels[def.id] ?? 0) > 0;
      const open = skillUnlocked(def, levels);
      const line = document.createElementNS(NS, 'line');
      line.setAttribute('x1', `${cx(pp)}`);
      line.setAttribute('y1', `${cy(pp)}`);
      line.setAttribute('x2', `${cx(cp)}`);
      line.setAttribute('y2', `${cy(cp)}`);
      line.setAttribute('class', `link ${owned ? 'owned' : open ? 'open' : 'locked'}`);
      svg.appendChild(line);
    }
    canvas.appendChild(svg);

    // Nodes.
    for (const def of SKILLS) {
      const p = this.skillLayout[def.id];
      if (!p) continue;
      const lvl = levels[def.id] ?? 0;
      const maxed = lvl >= def.maxLevel;
      const owned = lvl > 0;
      const open = skillUnlocked(def, levels);
      const cost = maxed ? 0 : skillCost(def, lvl);
      const affordable = !maxed && open && state.meta.skillPoints >= cost;
      let cls = 'locked';
      if (maxed) cls = 'maxed';
      else if (owned) cls = 'owned';
      else if (open) cls = affordable ? 'avail' : 'poor';
      const node = document.createElement('button');
      node.className =
        `snode interactive ${p.pillar} ${cls}` +
        (this.selectedSkill === def.id ? ' sel' : '');
      node.style.left = `${PAD + p.x * CELL_W}px`;
      node.style.top = `${PAD + p.y * CELL_H}px`;
      node.style.width = `${NODE}px`;
      node.style.height = `${NODE}px`;
      const face = !open && !owned ? '🔒' : def.icon;
      node.innerHTML =
        `<span class="ic">${face}</span>` + (owned ? `<span class="pip">${lvl}</span>` : '');
      node.addEventListener('click', () => {
        this.selectedSkill = def.id;
        this.renderSkills(state);
      });
      canvas.appendChild(node);
    }

    // Auto-select the first buyable node on open so the detail card is useful.
    if (!this.selectedSkill) {
      const first = SKILLS.find(
        (d) =>
          this.skillLayout[d.id] &&
          skillUnlocked(d, levels) &&
          (levels[d.id] ?? 0) < d.maxLevel,
      );
      this.selectedSkill = first?.id ?? SKILLS[0]?.id ?? null;
    }
    this.renderSkillDetail(state);
  }

  /** Detail card under the tree describing the currently selected node. */
  private renderSkillDetail(state: GameState): void {
    const box = this.q('#skill-detail');
    const def = this.selectedSkill ? SKILL_BY_ID[this.selectedSkill] : null;
    if (!def) {
      box.innerHTML = `<div class="sd-hint">${t('death.spendHint')}</div>`;
      return;
    }
    const levels = state.meta.skillLevels;
    const lvl = levels[def.id] ?? 0;
    const maxed = lvl >= def.maxLevel;
    const open = skillUnlocked(def, levels);
    const cost = maxed ? 0 : skillCost(def, lvl);
    const affordable = !maxed && open && state.meta.skillPoints >= cost;
    const name = tc('skill.' + def.id + '.name', def.name);
    const desc = tc('skill.' + def.id + '.desc', def.desc);
    box.innerHTML = `
      <div class="sd-head">
        <span class="sd-ic">${def.icon}</span>
        <div>
          <div class="sd-name">${name}</div>
          <div class="sd-lvl">${t('skill.lv', { lvl, max: def.maxLevel })}</div>
        </div>
      </div>
      <div class="sd-desc">${desc}</div>`;
    const btn = document.createElement('button');
    btn.className = 'buy interactive';
    if (maxed) {
      btn.textContent = t('skill.max');
      btn.disabled = true;
      btn.classList.add('maxed');
    } else if (!open && def.requires) {
      const req = SKILL_BY_ID[def.requires.id];
      const reqName = req ? tc('skill.' + req.id + '.name', req.name) : def.requires.id;
      btn.textContent = `🔒 ${reqName} · Lv${def.requires.level}`;
      btn.disabled = true;
    } else {
      btn.textContent = `${cost} ⭐`;
      btn.disabled = !affordable;
      btn.addEventListener('click', () => {
        this.ctrl.onBuySkill(def.id);
        this.renderSkills(state); // reflect new points, levels, and reveals
      });
    }
    box.appendChild(btn);
  }

  hideDeath(): void {
    this.deathOverlay.classList.remove('show');
  }

  // ---- offline report ----

  showOffline(seconds: number, gained: Record<string, number | undefined>): void {
    this.q('#offline-sub').textContent = t('offline.sub', { time: fmtDuration(seconds) });
    const list = this.q('#offline-list');
    list.innerHTML = '';
    let any = false;
    for (const id of RESOURCE_ORDER) {
      const g = gained[id];
      if (!g || g <= 0) continue;
      any = true;
      const row = document.createElement('div');
      row.className = 'ol';
      row.innerHTML = `${RESOURCES[id].icon} <b>+${fmt(g)}</b> ${tc('resource.' + id + '.name', RESOURCES[id].name)}`;
      list.appendChild(row);
    }
    if (!any) {
      list.innerHTML = `<div class="ol" style="color:var(--ink-dim)">${t('offline.nothing')}</div>`;
    }
    const btn = this.q<HTMLButtonElement>('#offline-btn');
    btn.onclick = () => this.offlineOverlay.classList.remove('show');
    this.offlineOverlay.classList.add('show');
  }

  // ---- transient feedback (banner + toasts) ----

  private bindBusForFeedback(): void {
    // The era boss is summoned to the gate: a distinct, dread-building event
    // before it actually marches in on the (longer) telegraph.
    bus.on('gate:summon', ({ boss }) => {
      this.toast(t('gate.summonTitle'), t('gate.summonSub', { boss }), 'bad');
    });
    bus.on('wave:incoming', ({ wave, isBoss }) => {
      if (isBoss) {
        this.showBanner(t('wave.bossIncoming'), t('banner.bossApproaches'), 'bad', 1.8);
      } else {
        const enemy = waveName(wave, this.lastState?.run.age ?? 0);
        this.showBanner(t('wave.incoming'), t('banner.onHorizon', { enemy }), 'bad', 1.6);
      }
    });
    bus.on('wave:result', ({ win, wave, isBoss }) => {
      const enemy = waveName(wave, this.lastState?.run.age ?? 0);
      if (isBoss) {
        this.showBanner(
          win ? t('gate.cleared') : t('banner.overrun'),
          win ? t('gate.clearedSub') : t('banner.brokeThrough', { enemy }),
          win ? 'good' : 'bad',
          1.6,
        );
      } else {
        this.showBanner(
          win ? t('banner.repelled') : t('banner.overrun'),
          win ? t('banner.scattered', { enemy }) : t('banner.brokeThrough', { enemy }),
          win ? 'good' : 'bad',
          1.4,
        );
      }
      // Occasional, non-intrusive feedback nudge at a couple of milestones.
      if (win && (wave === 3 || wave === 12) && !this.nudgesShown.has(wave)) {
        this.nudgesShown.add(wave);
        this.showNudge(wave === 3 ? t('nudge.early') : t('nudge.later'));
      }
    });
    bus.on('milestone', ({ title, subtitle, tone }) => {
      this.toast(title, subtitle, tone ?? 'good');
    });
    bus.on('settlement:evolve', ({ name }) => {
      this.showBanner(name, t('milestone.settlementGrows'), 'good', 1.6);
    });
    // Learn-by-doing: the first actual harvest advances the tutorial's core step.
    bus.on('harvest:hit', () => this.onTutorialHarvest());
  }

  private showBanner(big: string, small: string, tone: 'good' | 'bad', seconds: number): void {
    const bigEl = this.q('#banner-big');
    const smallEl = this.q('#banner-small');
    bigEl.textContent = big;
    bigEl.className = 'big ' + tone;
    smallEl.textContent = small;
    this.banner.classList.add('show');
    this.bannerTimer = seconds;
  }

  private toast(title: string, subtitle: string | undefined, tone: string): void {
    const el = document.createElement('div');
    el.className = 'toast ' + tone;
    el.textContent = subtitle ? `${title} - ${subtitle}` : title;
    this.toasts.appendChild(el);
    // CSS animation ends ~2.8s; remove a touch after.
    window.setTimeout(() => el.remove(), 3000);
  }

  /** Ticks transient UI timers (banner auto-hide, feedback nudge). Per frame. */
  tickFeedback(frameDt: number): void {
    if (this.bannerTimer > 0) {
      this.bannerTimer -= frameDt;
      if (this.bannerTimer <= 0) this.banner.classList.remove('show');
    }
    if (this.nudgeTimer > 0) {
      this.nudgeTimer -= frameDt;
      if (this.nudgeTimer <= 0) this.q('#nudge').classList.remove('show');
    }
  }
}
